/**
 * Turning the nav manifest into the items a surface can render (SAL-2912).
 *
 * `lib/navigation/nav-manifest.js` says what the menus contain; this says what
 * one particular person, in one particular studio, gets to see. It is the only
 * place that knows how a `requires` name is answered, so the header and the ⌘K
 * index cannot disagree about who sees what — they call the same function with
 * a context assembled from wherever their data happens to come from (the
 * session on the client, Prisma on the server).
 *
 * Pure and dependency-free beyond a few plain helpers, so it runs in client
 * components, server code and unit tests alike.
 */

import {
  getModuleLabelForAdminHref,
  getModuleLabelForDashboardHref,
  getModuleLabelForPlaybookId,
  getModuleLabelForReportHref,
} from "@/lib/modules/display-names";
import { isDeveloperUserId } from "@/lib/feature-access";
import { NAV_MANIFEST } from "@/lib/navigation/nav-manifest";
import { filterNavItemsByPlugins, isPluginEnabled } from "@/lib/plugins";
import { isTrainingModeRestrictedUser } from "@/lib/training-mode-users";
import { applyMenuOverride } from "@/lib/workspaces/menu-override";
import { hasRolePrivilege } from "@/middleware/rolePermissions";

/**
 * The vocabulary a manifest entry's `requires` draws from. Each answers one
 * question about the viewer or the studio; anything a menu needs to know has to
 * be expressible here, which is what keeps the manifest plain data.
 *
 * Every condition receives the built context and the entry's resolved href, so
 * a module gate can be answered from the destination itself rather than by
 * repeating it in the manifest.
 *
 * @type {Record<string, (ctx: object, href?: string) => boolean>}
 */
export const NAV_CONDITIONS = {
  /** Signed in at all — including before a studio has been chosen. */
  user: (ctx) => Boolean(ctx.userId),
  /** Signed in and inside a studio. */
  workspace: (ctx) => Boolean(ctx.workspaceId),

  admin: (ctx) => ctx.isAdmin,
  /**
   * Everyone the supervisor role outranks-or-equals — supervisors and admins
   * both. Spelled out in the name because `supervisor` alone read as "and not
   * an admin", which is the opposite of what it gates.
   */
  supervisorOrAbove: (ctx) => ctx.isAdminOrSupervisor,
  adminOrSupervisorOrLeader: (ctx) =>
    ctx.isAdminOrSupervisor || ctx.isCommunityLeader,
  /**
   * Exists for the one destination that differs by role rather than being
   * hidden by it: non-admins reach Studio Settings at a different URL.
   */
  notAdmin: (ctx) => !ctx.isAdmin,
  notTrainingModeRestricted: (ctx) => !ctx.isTrainingModeRestricted,
  /**
   * One of us, by user id (`lib/feature-access.js`) — for a feature being
   * tried out before anyone else sees it. Not a role: it does not widen with
   * superUser, and impersonation cannot reach it, because the id is the
   * impersonated member's. The page behind such a link guards itself with the
   * same check, since hiding a menu entry hides nothing from a typed URL.
   */
  developer: (ctx) => isDeveloperUserId(ctx.userId),

  badges: (ctx) => Boolean(ctx.workspace?.badgesEnabled),
  stories: (ctx) => ctx.modules.stories,
  dealClinic: (ctx) => ctx.modules.dealClinic,
  management: (ctx) => ctx.modules.management,
  conversationAnalysis: (ctx) => ctx.modules.conversationAnalysis,
  onTheFly: (ctx) => ctx.modules.onTheFly,
  scriptGenerator: (ctx) => ctx.modules.scriptGenerator,
  anyExecutionModule: (ctx) =>
    ctx.modules.dealClinic ||
    ctx.modules.conversationAnalysis ||
    ctx.modules.onTheFly ||
    ctx.modules.scriptGenerator,

  /** This studio has the module behind this personal-results page. */
  dashboardModule: (ctx, href) => ctx.enabledDashboardHrefs.has(href),
  /** This studio has the module behind this team report. */
  reportModule: (ctx, href) => ctx.enabledReportHrefs.has(href),
};

const MODULE_LABEL_RESOLVERS = {
  dashboard: getModuleLabelForDashboardHref,
  report: getModuleLabelForReportHref,
  admin: getModuleLabelForAdminHref,
  // Playbook links are anchors on one page, so the module is named by the
  // fragment rather than by the path.
  playbook: (enabledModules, href, label) =>
    getModuleLabelForPlaybookId(enabledModules, href?.split("#")[1], label),
};

/**
 * Build the context every condition is answered against.
 *
 * The role and module flags are passed in rather than fetched, because the two
 * callers learn them very differently: the header reads the NextAuth session and
 * a handful of server actions, the search index reads one Prisma query.
 *
 * @param {object} input
 * @param {{id?: string, workspaceId?: string, role?: string, superUser?: boolean, trainingMode?: boolean}} [input.user]
 * @param {object} [input.workspace]  A Workspace row — `plugins`, `menuOverride`
 *   and `badgesEnabled` are read.
 * @param {Array} [input.enabledModules]  Normalized module records.
 * @param {object} [input.modules]  Optional module switches that are not
 *   derivable from `enabledModules` alone.
 * @param {boolean} [input.isAdmin]  Defaults to the admin role.
 * @param {boolean} [input.isAdminOrSupervisor]  Defaults to the role hierarchy.
 *   Both are overridable because the search index counts superusers here and
 *   the header does not: a JWT keeps `superUser: true` through impersonation,
 *   so trusting it in the header would show the impersonated member menus their
 *   own role never opens.
 * @param {boolean} [input.isCommunityLeader]
 */
export function createNavContext({
  user = {},
  workspace = null,
  enabledModules = [],
  modules = {},
  isAdmin,
  isAdminOrSupervisor: isAdminOrSupervisorInput,
  isCommunityLeader = false,
} = {}) {
  const isAdminOrSupervisor =
    typeof isAdminOrSupervisorInput === "boolean"
      ? isAdminOrSupervisorInput
      : hasRolePrivilege(user?.role, "supervisor");

  const moduleFlags = {
    stories: Boolean(modules.stories),
    dealClinic: Boolean(modules.dealClinic),
    management: Boolean(modules.management),
    conversationAnalysis: Boolean(modules.conversationAnalysis),
    onTheFly: Boolean(modules.onTheFly),
    scriptGenerator: Boolean(modules.scriptGenerator),
  };

  // A module row's own href names the simulator it drives; the results page and
  // the team report for it are the same last segment under another root.
  const moduleSegments = enabledModules
    .filter((module) => module?.href)
    .map((module) => module.href.split("/").pop());

  const enabledDashboardHrefs = new Set([
    ...moduleSegments.map((segment) => `/dashboard/${segment}`),
    ...(isPluginEnabled(workspace, "okrs") ? ["/dashboard/okrs"] : []),
    ...(workspace?.badgesEnabled ? ["/dashboard/badges"] : []),
    // Management practice is a manager's own scores, so it is role-gated on top
    // of the module.
    ...(moduleFlags.management && isAdminOrSupervisor
      ? ["/dashboard/management"]
      : []),
    ...(moduleFlags.conversationAnalysis
      ? ["/dashboard/transcript-results"]
      : []),
    ...(moduleFlags.onTheFly ? ["/dashboard/plays/on-the-fly"] : []),
    ...(moduleFlags.dealClinic ? ["/dem-cog/reports"] : []),
  ]);

  const enabledReportHrefs = new Set([
    ...moduleSegments.map((segment) => `/workspaces/reports/${segment}`),
    ...(moduleFlags.conversationAnalysis
      ? ["/workspaces/reports/transcript-results"]
      : []),
  ]);

  return {
    userId: user?.id,
    workspaceId: user?.workspaceId,
    isTrainingModeRestricted: isTrainingModeRestrictedUser(user),
    workspace,
    enabledModules,
    modules: moduleFlags,
    isAdmin: typeof isAdmin === "boolean" ? isAdmin : user?.role === "admin",
    isAdminOrSupervisor,
    isCommunityLeader: Boolean(isCommunityLeader),
    enabledDashboardHrefs,
    enabledReportHrefs,
  };
}

// Nav is rebuilt on every render, so an unknown name is reported once rather
// than once per item per render.
const warnedConditions = new Set();

/**
 * Evaluate a manifest `requires` descriptor. Absent means "always".
 *
 * A name that is not in `NAV_CONDITIONS` hides the entry. Most of this
 * vocabulary gates something by role, so a typo has to fail closed: `"admim"`
 * silently opening the admin menu to everyone is a far worse outcome than one
 * link going missing, and `navigationManifest.test.js` fails on any name the
 * manifest uses that this file does not define.
 *
 * `all` and `any` may be combined; both then have to hold.
 */
export function isRequirementMet(requires, ctx, href) {
  if (!requires) return true;

  if (typeof requires === "string") {
    const condition = NAV_CONDITIONS[requires];
    if (!condition) {
      if (!warnedConditions.has(requires)) {
        warnedConditions.add(requires);
        console.warn(`[nav] Unknown nav condition "${requires}" — hiding entry.`);
      }
      return false;
    }
    return Boolean(condition(ctx, href));
  }

  if (Array.isArray(requires)) {
    return requires.every((entry) => isRequirementMet(entry, ctx, href));
  }

  const passesAll =
    !Array.isArray(requires.all) ||
    requires.all.every((entry) => isRequirementMet(entry, ctx, href));
  const passesAny =
    !Array.isArray(requires.any) ||
    requires.any.some((entry) => isRequirementMet(entry, ctx, href));

  return passesAll && passesAny;
}

function resolveHref(href, ctx) {
  if (typeof href !== "string") return undefined;
  return href.includes("{workspaceId}")
    ? href.replace("{workspaceId}", ctx.workspaceId || "")
    : href;
}

function buildItem(item, ctx, relabel) {
  const href = resolveHref(item.href, ctx);
  const subItems = Array.isArray(item.subItems)
    ? item.subItems.map((sub) => buildItem(sub, ctx, relabel))
    : null;

  // A group is only as visible as its contents: hiding it when nothing survives
  // is what stops a section heading rendering above an empty list, and it means
  // a group's own `requires` only has to state what its children cannot.
  const display =
    isRequirementMet(item.requires, ctx, href) &&
    (!subItems || subItems.some((sub) => sub.display));

  return {
    ...(href ? { href } : {}),
    label: relabel(href, item.label),
    ...(item.iconName ? { iconName: item.iconName } : {}),
    ...(item.shortcut ? { shortcut: item.shortcut } : {}),
    ...(item.external ? { external: true } : {}),
    display,
    ...(subItems ? { subItems } : {}),
  };
}

/**
 * Re-derive each group's `display` from the children that survived filtering.
 *
 * `buildItem` decides visibility before the plugin and menuOverride passes run,
 * and those drop links by href without consulting `display`. A group can
 * therefore come out of them holding nothing but already-hidden children and
 * still claim to be visible — which the mobile drawer would render as a leaf,
 * i.e. a link with no href.
 */
function rederiveGroupDisplay(items) {
  return items.map((item) => {
    if (!Array.isArray(item.subItems)) return item;

    const subItems = rederiveGroupDisplay(item.subItems);
    return {
      ...item,
      subItems,
      display: item.display && subItems.some((sub) => sub.display),
    };
  });
}

/**
 * Which menu owns this route.
 *
 * Menus overlap by design — `/workspaces/reports` sits inside Admin's
 * `/workspaces`, `/dashboard/learning-paths` inside My Results' `/dashboard` —
 * so the most specific prefix wins and exactly one menu is ever marked active.
 * Matching stops at a path segment, so `/dashboards` is not `/dashboard`.
 *
 * @param {Array} sections  From `buildNavSections`.
 * @param {string} pathname  With any locale prefix already stripped.
 * @returns {string | null}  The winning menu's `key`.
 */
export function resolveActiveNavKey(sections, pathname) {
  if (typeof pathname !== "string" || !pathname) return null;

  let activeKey = null;
  let matchedLength = 0;

  for (const menu of sections || []) {
    for (const prefix of menu.activePrefixes || []) {
      const matches =
        pathname === prefix ||
        pathname.startsWith(prefix.endsWith("/") ? prefix : `${prefix}/`);
      if (matches && prefix.length > matchedLength) {
        activeKey = menu.key;
        matchedLength = prefix.length;
      }
    }
  }

  return activeKey;
}

/**
 * The menus this person sees, in header order, with every item resolved,
 * relabelled, plugin-filtered and trimmed to the studio's allow-list.
 *
 * @param {object} ctx  From `createNavContext`.
 * @returns {Array<{key: string, menuLabel: string, section: string, subtitle?: string, activePrefixes: string[], crossLink?: object, items: Array}>}
 */
export function buildNavSections(ctx) {
  return NAV_MANIFEST.filter((menu) =>
    isRequirementMet(menu.requires, ctx),
  ).map((menu) => {
    const resolver = MODULE_LABEL_RESOLVERS[menu.moduleLabels];
    const relabel = resolver
      ? (href, label) => resolver(ctx.enabledModules, href, label)
      : (_href, label) => label;

    const built = menu.items.map((item) => buildItem(item, ctx, relabel));

    // Per-studio trimming, in two passes:
    //   1. plugin switches — drop links owned by a plugin this studio has off
    //      (lib/plugins/registry.js decides which hrefs those are), and
    //   2. menuOverride — trim what is left to the studio's allow-list.
    // Both are no-ops when nothing is configured.
    const withPlugins = filterNavItemsByPlugins(built, ctx.workspace);
    const trimmed = menu.overrideExempt
      ? withPlugins
      : applyMenuOverride(withPlugins, ctx.workspace);
    const items = rederiveGroupDisplay(trimmed);

    return {
      key: menu.key,
      menuLabel: menu.menuLabel,
      section: menu.section,
      ...(menu.subtitle ? { subtitle: menu.subtitle } : {}),
      activePrefixes: menu.activePrefixes,
      ...(menu.crossLink && isRequirementMet(menu.crossLink.requires, ctx)
        ? {
            // Resolved like any other href, so a cross-link may point at a
            // per-studio route without rendering "{workspaceId}" literally.
            crossLink: {
              ...menu.crossLink,
              href: resolveHref(menu.crossLink.href, ctx),
            },
          }
        : {}),
      items,
    };
  });
}

/** Does this menu have anything left to show? */
export function hasVisibleNavItems(items) {
  return Array.isArray(items) && items.some((item) => item && item.display);
}

/**
 * The page shortcuts this person can use, from the menus they see: an entry a
 * gate hides takes its shortcut with it.
 *
 * @returns {Array<{letter: string, href: string, label: string, menuKey: string, menuLabel: string, section: string}>}
 */
export function collectNavShortcuts(sections) {
  const shortcuts = [];
  const walk = (items, menu) => {
    for (const item of items || []) {
      if (!item?.display) continue;
      if (item.shortcut && item.href) {
        shortcuts.push({
          letter: item.shortcut,
          href: item.href,
          label: item.label,
          menuKey: menu.key,
          menuLabel: menu.menuLabel,
          section: menu.section,
        });
      }
      walk(item.subItems, menu);
    }
  };
  (sections || []).forEach((menu) => walk(menu.items, menu));
  return shortcuts;
}
